import { useState } from 'react';
import gql from 'not-graphql-tag';
import useAccountCustomer from '../hooks/useAccountCustomer';
import useSelectedAccountParams from '../hooks/useSelectedAccountParams';
import useAuthQuery from '../hooks/useAuthQuery';
import { useTranslation } from '../hooks/useTranslation';
import { useApolloClient } from '@apollo/react-hooks';
import {
  DeletePersonDetailsResponse,
  LinkedPerson,
} from '../__generated__/pge-types';

export const getLinkedPersons = gql`
  query GetLinkedPersons($params: LinkedPersonRequest) {
    getLinkedPersons(params: $params) {
      contactPhone
      email
      name
      encryptedPersonId
      encryptedContactId
      personId
      isFinanciallyResponsible
      isCssUser
      accountRelTypeCd
      verificationStatus
      inputData
    }
  }
`;

export const deletePersonDetailsMutation = gql`
  mutation Mutation($payload: DeletePersonDetailsRequest) {
    deletePersonDetails(payload: $payload) {
      success
    }
  }
`;

export function usePersonManage() {
  const { t, richT } = useTranslation();
  const { customer } = useAccountCustomer();
  const { accountParams } = useSelectedAccountParams();

  const [displayError, setDisplayError] = useState<
    string | React.ReactNode | undefined
  >(undefined);

  const { data: personData, refetch: refetchPerson } = useAuthQuery<{
    getLinkedPersons: Array<LinkedPerson>;
  }>(getLinkedPersons, {
    variables: {
      params: {
        accountId: accountParams?.accountNumber,
      },
    },
    errorPolicy: 'all',
    skip: !accountParams,
    fetchPolicy: 'no-cache',
  });

  const mainPersonEmail =
    (personData &&
      personData?.getLinkedPersons.find(per => per.accountRelTypeCd === 'MAIN')
        ?.email) ||
    '';
  const apolloClient = useApolloClient();

  const handleChangePersonDetails = async () => {
    setDisplayError(undefined);
  };

  const handleDeletePersonDetails = async (selectedPerson: LinkedPerson) => {
    if (selectedPerson.isFinanciallyResponsible) {
      setDisplayError(
        richT('REMOVE_PERSON_FINANCIALLY_RESPONSIBLE_MESSAGE', {
          PERSON_NAME: selectedPerson.name ?? '',
          ACCOUNT_NUMBER: accountParams?.accountNumber,
          CUSTOMER_SERVICE_NUMBER: t('CALL_FOR_ASSISTANCE_NUMBER'),
        }),
      );
    } else {
      const { data: deleteData } = await apolloClient.mutate<{
        deletePersonDetails: DeletePersonDetailsResponse;
      }>({
        mutation: deletePersonDetailsMutation,
        variables: {
          payload: {
            accountNumber: accountParams?.accountNumber,
            encryptedPersonId: selectedPerson.personId,
            firstName: selectedPerson.name!.split(', ')[0] || '',
            lastName: selectedPerson.name!.split(', ')[1] || '',
            mainPersonEmailId: mainPersonEmail,
            updatedBy: customer?.encryptedPersonId,
            relationshipType: selectedPerson.accountRelTypeCd,
          },
        },
        refetchQueries: [
          {
            query: getLinkedPersons,
            variables: {
              params: {
                accountId: accountParams?.accountNumber,
              },
            },
          },
        ],
        awaitRefetchQueries: true,
      });
      if (deleteData?.deletePersonDetails?.success) {
        await refetchPerson();
      }
    }
  };

  return {
    personList: personData?.getLinkedPersons,
    handleChangePersonDetails,
    handleDeletePersonDetails,
    displayError,
    refetchPerson,
    customer,
    mainPersonEmail,
  };
}
